/**
 * Inbound Logistics (Procurement) References — thin wrapper over the shared
 * `@/components/references` shell, driven by the canonical KHO (catalog).
 * Owns state (pageKey/search), the RTK query, and the localStorage column +
 * width controllers.
 */
import { useMemo, useState } from 'react'

import {
  INBOUND_REFERENCE_ORDER,
  REFERENCE_CATALOG,
  type ReferencePageKey,
} from '@/components/references/catalog'
import ReferencesView from '@/components/references/ReferencesView'
import type { ReferenceMenuColumn } from '@/components/references/types'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useLocalDatasheetColumns } from '@/utils/datasheetColumns/useLocalDatasheetColumns'
import { useGetProcurementReferenceRowsQuery } from '../../store/api/procurementApi'
import type { ProcurementFilters } from '@/pages/procurement/ProcurementPage'

interface Props {
  filters: ProcurementFilters
  /** Controlled selected page (lifted lên ProcurementPage để header enable/disable filter). */
  pageKey?: ReferencePageKey
  onPageKeyChange?: (key: ReferencePageKey) => void
}

export default function ProcurementReferencesView({
  filters,
  pageKey: controlledPageKey,
  onPageKeyChange,
}: Props) {
  const [internalPageKey, setInternalPageKey] = useState<ReferencePageKey>('contract-statistics')
  const pageKey = controlledPageKey ?? internalPageKey
  const setPageKey = (key: ReferencePageKey) => {
    setInternalPageKey(key)
    onPageKeyChange?.(key)
  }
  const [searchByPage, setSearchByPage] = useState<Partial<Record<ReferencePageKey, string>>>({})

  const page = REFERENCE_CATALOG[pageKey]
  const search = searchByPage[pageKey] ?? ''

  // Filter mỗi page tự khai trong catalog — chỉ forward param page cần.
  const pageFilters = page.filters ?? []
  const useDate = pageFilters.includes('date')
  const useForced = pageFilters.includes('forcedClose')
  const queryArg = useMemo(
    () => ({
      pageKey,
      limit: page.limit ?? 1_000_000,
      ...(useDate
        ? { startDate: filters.startDate || undefined, endDate: filters.endDate || undefined }
        : {}),
      ...(useForced
        ? {
            isForcedClose:
              filters.isForcedClose === 'yes' ? true : filters.isForcedClose === 'no' ? false : undefined,
          }
        : {}),
    }),
    [page.limit, pageKey, useDate, useForced, filters.startDate, filters.endDate, filters.isForcedClose],
  )

  const { currentData, isFetching, error, refetch } = useGetProcurementReferenceRowsQuery(queryArg, {
    refetchOnMountOrArgChange: true,
    skip: useDate && !!filters.startDate && !!filters.endDate && filters.startDate > filters.endDate,
  })

  const menuColumns = useMemo<ReferenceMenuColumn[]>(
    () => page.columns.map((c) => ({ ...c, key: c.field, label: c.headerName })),
    [page.columns],
  )
  // Column layout keyed theo canonical page-key (dùng chung xuyên function):
  // cùng 1 page mở ở function nào cũng chung 1 layout width + ẩn/hiện, per-user.
  const columnsCtrl = useLocalDatasheetColumns({
    allColumns: menuColumns,
    storageKey: `references.${pageKey}`,
  })
  const widths = useColumnWidths(`references.${pageKey}`)

  const rows = currentData?.items ?? []
  return (
    <ReferencesView<ReferencePageKey>
      title="Inbound Logistics References"
      subtitle="Access switchboard parity for the 16 Inbound Logistics reference pages."
      pageOrder={INBOUND_REFERENCE_ORDER}
      configs={REFERENCE_CATALOG}
      pageKey={pageKey}
      onPageKeyChange={setPageKey}
      rows={rows}
      search={search}
      onSearchChange={(value) => setSearchByPage((prev) => ({ ...prev, [pageKey]: value }))}
      loading={isFetching && rows.length === 0}
      fetching={isFetching && rows.length > 0}
      error={error}
      onRetry={() => {
        void refetch()
      }}
      columnsCtrl={columnsCtrl}
      widths={widths}
      totalsMode="sum-flag"
    />
  )
}
